import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Download from "../../images/Vectors/Download";
import { API_BASE_URL } from "../../Utils/useApi";

const DownloadBrochure = ({ open, onClose, downloadUrl, productTitle }) => {
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_states.php`);
      const data = await response.json();
      setStates(data);
    } catch (error) {
      console.error("Error fetching states:", error);
      showNotification("Failed to fetch states", "error");
    }
  };

  const fetchCities = async (stateid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_cities.php?sid=${stateid}`);
      const data = await response.json();
      setCities(data);
    } catch (error) {
      console.error("Error fetching cities:", error);
      showNotification("Failed to fetch cities", "error");
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .min(3, "Name must be at least 3 characters")
      .max(50, "Name must not exceed 50 characters")
      .matches(/^[A-Z a-z]+$/, "Name must contain only alphabets"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^\d{10}$/, "Mobile number must be 10 digits"),
    email: Yup.string().email("Invalid email format").nullable(),
    state: Yup.string().nullable(),
city: Yup.string().nullable(),
    message: Yup.string().nullable(),
  });

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ open: false, message: "", severity: "success" });
  };

  const triggerDownload = (url) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = true;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Box>
            <h3 style={{ margin: "0", fontSize: "1.5rem", fontWeight: "600" }}>
              Download Brochure
            </h3>
            {productTitle && (
              <p
                style={{
                  margin: "4px 0 15px 0",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                {productTitle}
              </p>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#666",
              "&:hover": { color: "#000" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Formik
            initialValues={{
              name: "",
              mobile: "",
              email: "",
              state: "",
              city: "",
              message: "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              setSubmitting(true);
              const formData = {
                ...values,
                product_name: productTitle || "Product Brochure",
              };

              try {
                const response = await fetch(
                  `${API_BASE_URL}/download_brochure.php`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(formData),
                  }
                );
                const data = await response.json();

                if (data.status === "success") {
                  showNotification(
                    "Thank you! Your download will begin shortly.",
                    "success"
                  );
                  setTimeout(() => {
                    triggerDownload(downloadUrl);
                    resetForm();
                    onClose();
                  }, 1500);
                } else {
                  showNotification(
                    data.message || "Form submission failed. Please try again.",
                    "error"
                  );
                }
              } catch (error) {
                console.error("Error:", error);
                showNotification("Server error. Please try again later.", "error");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ errors, touched, handleSubmit, isSubmitting, setFieldValue, values }) => (
              <Form onSubmit={handleSubmit}>
                {/* Row 1: Name + Mobile */}
                <Grid container spacing={2} sx={{mb: 2 }} className="rowFormControl pt-2">
                  <Grid item xs={12} sm={6} sx={{pt: 10 }}>
                    <Field name="name">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="FULL NAME*"
                          variant="outlined"
                          size="small"
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name="mobile">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="MOBILE NO*"
                          variant="outlined"
                          size="small"
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                        />
                      )}
                    </Field>
                  </Grid>
                </Grid>

                {/* Row 2: Email */}
                <Box sx={{ mb: 2 }} className="rowFormControl">
                  <Field name="email">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="EMAIL ID"
                        variant="outlined"
                        size="small"
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Box>

                {/* Row 3: State + City */}
                <Grid container spacing={2} sx={{ mb: 2 }} className="rowFormControl">
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" error={touched.state && Boolean(errors.state)}>
                      <InputLabel id="state-label">SELECT STATE</InputLabel>
                      <Field name="state">
                        {({ field }) => (
                          <Select
                            {...field}
                            labelId="state-label"
                            onChange={(e) => {
                              const selectedState = states.find(
                                (state) => state.sname === String(e.target.value)
                              );
                              if (selectedState) {
                                fetchCities(selectedState.stateid);
                                setFieldValue("city", "");
                              }
                              field.onChange(e);
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {states.map((state) => (
                              <MenuItem key={state.stateid} value={state.sname}>
                                {state.sname}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </Field>
                      <FormHelperText>{touched.state && errors.state}</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} className="rowFormControl">
                    <FormControl fullWidth size="small" error={touched.city && Boolean(errors.city)}>
                      <InputLabel id="city-label">SELECT CITY</InputLabel>
                      <Field name="city">
                        {({ field }) => (
                          <Select
                            {...field}
                            labelId="city-label"
                            disabled={!values.state || cities.length === 0}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {cities.map((city) => (
                              <MenuItem key={city.cityid} value={city.cityname}>
                                {city.cityname}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </Field>
                      <FormHelperText>{touched.city && errors.city}</FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Row 4: Message */}
                <Box sx={{ mb: 2 }} className="rowFormControl">
                  <Field name="message">
                    {({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="MESSAGE"
                        multiline
                        rows={3}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Field>
                </Box>

                <DialogActions
                  sx={{
                    px: 0,
                    pb: 0,
                    pt: 2,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    className="customButton downloadForm downloadBotton"
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <>
                        <Download />
                        Download Now
                      </>
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseNotification}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DownloadBrochure;
